import { useState } from 'react'
import { Building2 } from 'lucide-react'
import { switchActiveOrg, useUserOrgs } from '@/lib/org'
import { useAuth } from '@/lib/auth'

// =============================================================================
// OrgSelector
//
// One consistent org-selector chrome mounted on every /account/* page
// that has org-scoped content. Selecting an org from this dropdown:
//
//   1. Writes user_profiles.active_organization_id (shared across pages —
//      navigating to another /account/* page picks up the same selection).
//   2. Fires the `onChange` callback so the host page can update its
//      local view state (filter the key list, swap usage data, etc.).
//
// "All organizations" is opt-in via the `allowAll` prop. Selecting it
// fires onChange but does NOT write active_organization_id — it's a
// pure filter; the user's "current org" stays whatever they last picked
// specifically.
//
// Hidden entirely when the user only belongs to one org (nothing to
// select between); the host page typically renders the single org name
// as a read-only label in that case.
// =============================================================================

/** Sentinel value passed to onChange when the user selects "All organizations". */
export const ALL_ORGS = '__all__'

interface OrgSelectorProps {
  /** Current value — pass the org id, or ALL_ORGS for the "all" option. */
  value: string
  /** Fires on selection. The value is either an org id or ALL_ORGS. */
  onChange: (value: string) => void
  /** Label text displayed left of the dropdown. */
  label?: string
  /** Show "All organizations" as the final option. Default false. */
  allowAll?: boolean
}

export function OrgSelector({
  value,
  onChange,
  label = 'Organization',
  allowAll = false,
}: OrgSelectorProps) {
  const { user } = useAuth()
  const { orgs, loading } = useUserOrgs()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  if (loading) return null
  if (orgs.length === 0) return null

  // Single-org users: read-only chrome. Surfacing a dropdown with one
  // option is noise; the org name as a label is enough context.
  if (orgs.length === 1) {
    return (
      <div className="inline-flex items-center gap-2 text-[12.5px] text-[var(--color-muted)]">
        <Building2 size={13} aria-hidden />
        <span>{orgs[0].name}</span>
      </div>
    )
  }

  async function handle(next: string) {
    setErr(null)
    // Write user_profiles.active_organization_id FIRST, then fire
    // onChange. Parent pages' onChange handlers re-fetch org-scoped
    // data (via refreshOrg / refreshMembers / etc.) which reads back
    // active_organization_id — firing onChange first meant the parent
    // refresh ran against the stale active org, so switching looked
    // broken until the next manual refresh. Skip the DB write for the
    // "All" sentinel since it's filter-only.
    if (user && next !== ALL_ORGS) {
      setBusy(true)
      const { error } = await switchActiveOrg(user.id, next)
      setBusy(false)
      if (error) {
        setErr(error)
        return
      }
    }
    onChange(next)
  }

  return (
    <div className="inline-flex items-center gap-2">
      <label className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
        <Building2 size={12} aria-hidden />
        {label}
      </label>
      <select
        value={value}
        disabled={busy}
        onChange={(e) => handle(e.target.value)}
        className={[
          'px-2.5 py-1 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-surface)] border border-[var(--color-border)]',
          'text-[12.5px] text-[var(--color-text)] cursor-pointer',
          'hover:border-[var(--color-accent)]/50',
          'focus:outline-none focus:border-[var(--color-accent)]',
          'disabled:opacity-60 disabled:cursor-not-allowed',
        ].join(' ')}
      >
        {orgs.map((o) => (
          <option key={o.organization_id} value={o.organization_id}>
            {o.name}
          </option>
        ))}
        {allowAll ? <option value={ALL_ORGS}>All organizations</option> : null}
      </select>
      {err ? <span className="text-[11px] text-[var(--color-signal)]">{err}</span> : null}
    </div>
  )
}
